/**
 * TanStack Query hooks for /api/cars/* endpoints.
 *
 * Mutations invalidate the relevant queries so the UI updates without a
 * full reload:
 *   - useCreateCar / useUpdateCar / useDeleteCar → ["cars"] + ["car", id]
 *   - useUploadCarImage / useDeleteCarImage     → ["car", id]
 *   - share mutations                           → ["car", id]
 *   - useTransferCar                            → ["car", id]
 *   - useSetActiveCar                           → active-car dependent queries
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { fetchJson, postJson } from "./client";
import type {
  CarCreateInput,
  CarResponse,
  CarSummaryResponse,
  CarUpdateInput,
  CarsListResponse,
  ShareInviteInput,
  TransferInput,
} from "./types";

const carsKey = ["cars"] as const;
const carKey = (id: number) => ["car", id] as const;

export function useCars() {
  return useQuery({
    queryKey: carsKey,
    queryFn: () => fetchJson<CarsListResponse>("/api/cars"),
  });
}

export function useCar(id: number | undefined) {
  return useQuery({
    queryKey: id ? carKey(id) : ["car", "none"],
    queryFn: () => fetchJson<CarResponse>(`/api/cars/${id}`),
    enabled: typeof id === "number",
  });
}

export function useCreateCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CarCreateInput) =>
      postJson<CarSummaryResponse>("/api/cars", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carsKey });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useUpdateCar(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CarUpdateInput) =>
      fetchJson<CarSummaryResponse>(`/api/cars/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carsKey });
      qc.invalidateQueries({ queryKey: carKey(id) });
    },
  });
}

export function useDeleteCar(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson<{ ok: true }>(`/api/cars/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carsKey });
      qc.removeQueries({ queryKey: carKey(id) });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useUploadCarImage(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      // Use plain fetch since postForm only takes string fields.
      const csrf =
        typeof document !== "undefined"
          ? document.cookie
              .split("; ")
              .find((c) => c.startsWith("wsl_csrf="))
              ?.slice("wsl_csrf=".length)
          : undefined;
      const headers: Record<string, string> = { Accept: "application/json" };
      if (csrf) headers["X-CSRF-Token"] = decodeURIComponent(csrf);
      const res = await fetch(`/api/cars/${id}/image`, {
        method: "POST",
        headers,
        body: fd,
        credentials: "same-origin",
      });
      const text = await res.text();
      const payload = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const message =
          (payload && typeof payload === "object" && "detail" in payload
            ? String((payload as Record<string, unknown>).detail)
            : null) ?? `Upload failed (${res.status})`;
        throw new Error(message);
      }
      return payload as { ok: true; image_url: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(id) });
    },
  });
}

export function useDeleteCarImage(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson<{ ok: true }>(`/api/cars/${id}/image`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(id) });
    },
  });
}

export function useSetActiveCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      postJson<{ ok: true; active_car_id: number }>(
        `/api/cars/${id}/active`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["analysis"] });
    },
  });
}

export function useInviteShare(carId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShareInviteInput) =>
      postJson<{ ok: true; status: string }>(
        `/api/cars/${carId}/shares`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(carId) });
    },
  });
}

export function useRemoveShare(carId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      fetchJson<{ ok: true }>(`/api/cars/${carId}/shares/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(carId) });
      qc.invalidateQueries({ queryKey: carsKey });
    },
  });
}

export function useUpdateShareRole(carId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: number; role: "editor" | "viewer" }) =>
      fetchJson<{ ok: true }>(`/api/cars/${carId}/shares/${input.userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: input.role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(carId) });
      qc.invalidateQueries({ queryKey: carsKey });
    },
  });
}

export function useRevokeShareInvite(carId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) =>
      fetchJson<{ ok: true }>(
        `/api/cars/${carId}/share-invites/${notificationId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(carId) });
    },
  });
}

export function useTransferCar(carId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferInput) =>
      postJson<{ ok: true }>(`/api/cars/${carId}/transfer`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: carKey(carId) });
    },
  });
}
